(() => {
  document.documentElement.classList.replace('no-js', 'js');

  const compactNavigation = matchMedia('(max-width: 1050px)');
  const fineHover = matchMedia('(hover: hover) and (pointer: fine)');
  const hoverStates = new WeakMap();
  const summaryFor = (details) => details.querySelector(':scope > summary');

  function setDisclosure(details, open) {
    details.open = open;
    summaryFor(details)?.setAttribute('aria-expanded', String(open));
  }

  function cancelHover(header) {
    const state = hoverStates.get(header);
    if (!state) return;
    clearTimeout(state.openTimer);
    clearTimeout(state.closeTimer);
  }

  function initializeHeader(header) {
    cancelHover(header);
    hoverStates.delete(header);
    const openDelay = Number(header.dataset.fgHoverDelay);
    const closeDelay = Number(header.dataset.fgHoverCloseDelay);
    if (!Number.isFinite(openDelay) || openDelay < 100 || !Number.isFinite(closeDelay) || closeDelay < 150) {
      console.error('Foxtrot header hover delays are invalid; click and keyboard navigation remain available.', header);
    } else {
      hoverStates.set(header, { openDelay, closeDelay });
    }
    header.querySelectorAll('details').forEach((details) => {
      summaryFor(details)?.setAttribute('aria-expanded', String(details.open));
    });
    return hoverStates.get(header);
  }

  function initializeHeaders(root) {
    root.querySelectorAll('[data-fg-header]').forEach(initializeHeader);
  }

  function hoverStateFor(header) {
    const state = hoverStates.get(header);
    if (state && state.openDelay === Number(header.dataset.fgHoverDelay) && state.closeDelay === Number(header.dataset.fgHoverCloseDelay)) return state;
    // CLI live refresh can replace the header without a Theme Editor event.
    return initializeHeader(header);
  }

  function openDesktopDisclosure(details) {
    details.closest('[data-fg-header]').querySelectorAll('foxtrot-search[open]').forEach((search) => search.close());
    details.closest('[data-fg-header]').querySelectorAll('[data-fg-desktop-disclosure][open]').forEach((other) => {
      if (other !== details) setDisclosure(other, false);
    });
    setDisclosure(details, true);
  }

  function hasPanelFocus(details) {
    return details.contains(document.activeElement) && document.activeElement !== summaryFor(details);
  }

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' || compactNavigation.matches || !fineHover.matches || !(event.target instanceof Element)) return;
    const navigationItem = event.target.closest('.fg-nav > a, .fg-nav > details');
    if (navigationItem && !(event.relatedTarget instanceof Node && navigationItem.contains(event.relatedTarget))) {
      navigationItem.closest('[data-fg-header]').querySelectorAll('foxtrot-search[open]').forEach((search) => search.close());
    }
    const details = event.target.closest('[data-fg-desktop-disclosure]');
    if (!details || (event.relatedTarget instanceof Node && details.contains(event.relatedTarget))) return;
    const header = details.closest('[data-fg-header]');
    cancelHover(header);
    const state = hoverStateFor(header);
    if (!state || details.open) return;
    state.openTimer = setTimeout(() => {
      if (!header.isConnected) return;
      const focusedPanel = Array.from(header.querySelectorAll('[data-fg-desktop-disclosure][open]'))
        .some((other) => other !== details && hasPanelFocus(other));
      if (!focusedPanel) openDesktopDisclosure(details);
    }, state.openDelay);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || compactNavigation.matches || !fineHover.matches || !(event.target instanceof Element)) return;
    const details = event.target.closest('[data-fg-desktop-disclosure]');
    if (!details || (event.relatedTarget instanceof Node && details.contains(event.relatedTarget))) return;
    const header = details.closest('[data-fg-header]');
    cancelHover(header);
    const state = hoverStateFor(header);
    if (!state) return;
    state.closeTimer = setTimeout(() => {
      header.querySelectorAll('[data-fg-desktop-disclosure][open]').forEach((open) => {
        if (!hasPanelFocus(open)) setDisclosure(open, false);
      });
    }, state.closeDelay);
  });

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.closest('[data-fg-header]')) return;
    summaryFor(details)?.setAttribute('aria-expanded', String(details.open));
    if (!details.open) return;
    if (details.matches('[data-fg-desktop-disclosure]')) openDesktopDisclosure(details);
    if (details.matches('[data-fg-mobile-section]')) {
      details.closest('[data-fg-mobile-root]').querySelectorAll('[data-fg-mobile-section][open]').forEach((other) => {
        if (other !== details) setDisclosure(other, false);
      });
    }
  }, true);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    document.querySelectorAll('[data-fg-header]').forEach(cancelHover);
    document.querySelectorAll('[data-fg-header] details[open]').forEach((details) => {
      const headerLink = event.target.closest('[data-fg-header] a[href]');
      if (!details.contains(event.target) || headerLink) setDisclosure(details, false);
    });
  }, true);

  document.addEventListener('focusin', (event) => {
    if (!(event.target instanceof Element)) return;
    document.querySelectorAll('[data-fg-header] details[open]').forEach((details) => {
      if (!details.contains(event.target)) {
        cancelHover(details.closest('[data-fg-header]'));
        setDisclosure(details, false);
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    document.querySelectorAll('[data-fg-header]').forEach(cancelHover);
    const details = event.target.closest('[data-fg-header] details[open]')
      || document.querySelector('[data-fg-desktop-disclosure][open], [data-fg-mobile-root][open]');
    if (details) {
      event.preventDefault();
      setDisclosure(details, false);
      summaryFor(details)?.focus();
    }
  });

  function resetNavigation() {
    document.querySelectorAll('[data-fg-header]').forEach((header) => {
      cancelHover(header);
      const active = document.activeElement;
      const hiddenFocus = active instanceof Element && active.closest(compactNavigation.matches ? '.fg-nav' : '.fg-mobile-nav');
      header.querySelectorAll('details[open]').forEach((details) => setDisclosure(details, false));
      if (hiddenFocus && header.contains(active)) {
        header.querySelector(compactNavigation.matches ? '.fg-mobile-nav > summary' : '.fg-nav summary, .fg-nav > a')?.focus();
      }
    });
  }

  compactNavigation.addEventListener('change', resetNavigation);
  fineHover.addEventListener('change', resetNavigation);
  document.addEventListener('foxtrot:search:open', (event) => {
    const header = event.target.closest('[data-fg-header]');
    if (!header) return;
    cancelHover(header);
    header.querySelectorAll('details[open]').forEach((details) => setDisclosure(details, false));
  });
  document.addEventListener('shopify:section:load', (event) => initializeHeaders(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-fg-header]').forEach(cancelHover);
  });
  document.addEventListener('shopify:block:select', (event) => {
    if (compactNavigation.matches || !(event.target instanceof Element)) return;
    const details = event.target.closest('[data-fg-desktop-disclosure]');
    if (details) {
      cancelHover(details.closest('[data-fg-header]'));
      openDesktopDisclosure(details);
    }
  });
  initializeHeaders(document);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
    history.pushState({}, '', link.hash);
  });

  function rotateFieldPicks(root) {
    const candidates = Array.from(root.querySelectorAll('[data-fg-pick-candidate]'));
    if (candidates.length < 4) {
      console.error(`Field Tested Picks needs at least four available curated products; found ${candidates.length}.`, root);
      return;
    }
    for (let index = candidates.length - 1; index > 0; index--) {
      const swap = Math.floor(Math.random() * (index + 1));
      [candidates[index], candidates[swap]] = [candidates[swap], candidates[index]];
    }
    candidates.forEach((item, index) => {
      item.hidden = index >= 4;
      root.append(item);
    });
  }

  document.querySelectorAll('[data-fg-rotating-picks]').forEach(rotateFieldPicks);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-fg-rotating-picks]').forEach(rotateFieldPicks);
  });

  class FoxtrotProductRail extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.fg-product-list');
      this.previous = this.querySelector('[data-fg-previous]');
      this.next = this.querySelector('[data-fg-next]');
      if (!this.list || !this.previous || !this.next) {
        console.error('Foxtrot product rail is missing its list or navigation controls.', this);
        return;
      }
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.list.addEventListener('scroll', () => this.update(), { ...options, passive: true });
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.list);
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }

    move(direction) {
      const rtl = getComputedStyle(this.list).direction === 'rtl';
      this.list.scrollBy({
        left: this.list.clientWidth * direction * (rtl ? -1 : 1),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    }

    update() {
      const overflow = this.list.scrollWidth - this.list.clientWidth;
      const position = Math.abs(this.list.scrollLeft);
      this.previous.hidden = this.next.hidden = overflow < 2;
      this.previous.disabled = position < 2;
      this.next.disabled = position >= overflow - 2;
    }
  }

  if (!customElements.get('foxtrot-product-rail')) {
    customElements.define('foxtrot-product-rail', FoxtrotProductRail);
  }
})();
